// PROVIDERS — dedicated page describing the licensed provider network,
// the credentialing process, and how reviews are conducted. Reduces homepage
// clutter while still being one click away via /providers in the main nav.
import { ShieldCheck, Stethoscope, ClipboardCheck, MapPin, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/shared/SEO";

const RED = "#E11D2A";

const PILLARS = [
  {
    Icon: ShieldCheck,
    title: "State-Licensed Providers",
    body: "Every provider on the BestRx network holds an active, unencumbered license in your state of residence. Licensing is verified at onboarding and on a rolling basis thereafter.",
  },
  {
    Icon: Stethoscope,
    title: "Trained In Modern Therapies",
    body: "Our providers are specifically trained on GLP-1 agonists, peptide therapy, hormone optimization and cosmetic wellness — protocols that often sit outside standard primary-care training.",
  },
  {
    Icon: ClipboardCheck,
    title: "Independent Clinical Decisions",
    body: "Providers make their own treatment decisions. BestRx does not direct what to prescribe — we provide the platform, the patient intake, the pharmacy network and the logistics.",
  },
  {
    Icon: MapPin,
    title: "Coverage In Your State",
    body: "Licensed in all 50 states. If a provider is not currently available in your state, your assessment will be queued and we'll notify you the moment coverage is live.",
  },
];

const PROCESS = [
  { k: "01", t: "You complete the assessment",     d: "Quick medical questionnaire built from current clinical guidelines — usually 4–6 minutes." },
  { k: "02", t: "Provider reviews your intake",    d: "A licensed provider in your state reviews your history, goals and any flagged conditions." },
  { k: "03", t: "Treatment plan approved",          d: "If clinically appropriate the provider issues a prescription. If not, you'll receive a written explanation and alternatives." },
  { k: "04", t: "Medication shipped or escalated", d: "Approved orders ship from a US-licensed compounding pharmacy. Complex cases trigger a follow-up call." },
];

export default function ProvidersPage() {
  return (
    <main className="bg-white text-black min-h-screen" data-testid="providers-page">
      <SEO
        title="Our Provider Network"
        path="/providers"
        description="BestRx is powered by a 50-state network of licensed providers trained in GLP-1, peptide and hormone-optimization therapies. Learn how our credentialing and review process works."
        schema={{
          "@context": "https://schema.org",
          "@type": "MedicalBusiness",
          name: "BestRx Provider Network",
          areaServed: { "@type": "Country", name: "United States" },
        }}
      />
      <Nav />

      {/* Hero */}
      <section className="border-b border-black/5 bg-[#FAFAFC]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-6 lg:px-10 py-16 lg:py-20">
          <p className="text-[11px] tracking-[0.22em] font-extrabold uppercase" style={{ color: RED }}>Our Network</p>
          <h1 className="mt-3 font-heavy tracking-[-0.02em] leading-[0.95] text-black text-5xl sm:text-6xl">
            Licensed Providers.<br />
            <span style={{ color: RED }}>Independent Clinical Decisions.</span>
          </h1>
          <p className="mt-5 text-[15px] text-black/65 max-w-[640px]">
            Every prescription on BestRx is issued by a state-licensed provider after they personally review your medical intake. We never ship medication without provider sign-off.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-14 lg:py-20">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PILLARS.map(({ Icon, title, body }) => (
              <article key={title}
                data-testid={`provider-pillar-${title.toLowerCase().replace(/\W+/g, "-")}`}
                className="rounded-2xl border bg-white p-6"
                style={{ borderColor: "rgba(0,0,0,0.08)" }}>
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg"
                  style={{ background: `${RED}11`, color: RED }}>
                  <Icon size={18} strokeWidth={2.2} />
                </span>
                <h3 className="mt-4 font-heavy text-black text-[20px] tracking-tight">{title}</h3>
                <p className="mt-2 text-[13.5px] text-black/65 leading-relaxed">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* How review works */}
      <section className="py-14 lg:py-20 bg-[#FAFAFC] border-t border-black/5">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-6 lg:px-10">
          <header className="mb-10">
            <p className="text-[11px] tracking-[0.22em] font-extrabold uppercase" style={{ color: RED }}>How Review Works</p>
            <h2 className="mt-3 font-heavy tracking-[-0.02em] leading-[0.95] text-black text-4xl sm:text-5xl">
              4 Steps. <span style={{ color: RED }}>Provider On Every One.</span>
            </h2>
          </header>
          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PROCESS.map(({ k, t, d }) => (
              <li key={k} className="rounded-xl border bg-white p-5"
                style={{ borderColor: "rgba(0,0,0,0.08)" }}>
                <span className="font-heavy text-[12px] tracking-[0.18em]" style={{ color: RED }}>{k}</span>
                <h3 className="mt-2 font-heavy text-black text-[17px] tracking-tight">{t}</h3>
                <p className="mt-1.5 text-[13px] text-black/60 leading-relaxed">{d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-6 lg:px-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Link to="/#medication-zone"
            data-testid="providers-cta-start"
            className="flex items-center justify-between gap-3 px-6 py-5 rounded-xl bg-black text-white hover:-translate-y-0.5 transition-transform">
            <div>
              <p className="text-[10.5px] tracking-[0.22em] font-extrabold uppercase" style={{ color: RED }}>Begin</p>
              <p className="font-heavy text-[18px] mt-1">Start Your Assessment</p>
            </div>
            <ArrowUpRight size={20} />
          </Link>
          <Link to="/contact"
            data-testid="providers-cta-contact"
            className="flex items-center justify-between gap-3 px-6 py-5 rounded-xl border bg-white hover:-translate-y-0.5 transition-transform"
            style={{ borderColor: "rgba(0,0,0,0.08)" }}>
            <div>
              <p className="text-[10.5px] tracking-[0.22em] font-extrabold uppercase" style={{ color: RED }}>Questions?</p>
              <p className="font-heavy text-[18px] text-black mt-1">Reach Our Care Team</p>
            </div>
            <ArrowUpRight size={20} className="text-black/60" />
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}
